import type { CharacterSheetInterface } from '@/types/characters';
import type { ContainerEffect, SwadeModConfigurationInterface } from '@/types/items';
import { policyDeclarationFor } from '@/appdata/container-fields';
import { effectTargets, elementEntryUuid, selectionUuid } from '@/appdata/container-targets';

/**
 * Player-entered values for container modifications (spec 2026-09-15).
 *
 * A container.* modification authored with a null value on a text field ("the player defines the
 * trapping") registers an effect that contributes nothing: contributesSomething in
 * character-data.ts drops a null or blank value so it can never blank the field. The answer the
 * player types lives on that ledger entry, keyed by target container uuid (`ContainerEffect.entered`),
 * and these readers hand the rebuild an effect that carries it. The entry's own
 * `configuration.value` is never written — deriveContainerModKey stringifies it, and the narrowed
 * rollback finds the entry by that key.
 *
 * Pure. No store import, like container-carry.ts.
 */

const isBlankString = (value: unknown): boolean => typeof value === 'string' && value.trim().length === 0;

/**
 * A configuration the player may answer: a text-policy detail (`name`, `trapping`), an operator that
 * policy accepts (a blank-template operator would show a field that could never take effect — the
 * fold refuses it), and an authored value that is null, undefined or a blank string. A non-string,
 * non-null value (a number on a text field) is authored, however malformed: not enterable.
 */
export function isEnterable(configuration: SwadeModConfigurationInterface | null | undefined): boolean {
    const detail = configuration?.detail;
    if (typeof detail !== 'string') return false;
    const declaration = policyDeclarationFor(detail);
    if (declaration?.valueKind !== 'text') return false;
    const operator = configuration?.operator ?? null;
    if (typeof operator !== 'string' || !(declaration.operators as readonly string[]).includes(operator)) return false;
    const value = configuration?.value;
    return value === null || value === undefined || isBlankString(value);
}

/**
 * The string entered for one target, or null. The ledger is decoded JSON: a non-object map, a
 * missing key, an inherited key, a non-string or a blank value all read as absent.
 */
export function enteredValueFor(effect: ContainerEffect | null | undefined, containerUuid: string | null | undefined): string | null {
    if (typeof containerUuid !== 'string' || containerUuid.length === 0) return null;
    const map: unknown = effect?.entered;
    if (map === null || typeof map !== 'object' || Array.isArray(map)) return null;
    if (!Object.prototype.hasOwnProperty.call(map, containerUuid)) return null;
    const value = (map as Record<string, unknown>)[containerUuid];
    return typeof value === 'string' && !isBlankString(value) ? value : null;
}

/**
 * The effect as a rebuild should see it for one target: the SAME object when the configuration is
 * not enterable or nothing was entered for that target (identity, so a caller can tell nothing
 * changed), otherwise a shallow copy whose configuration carries the entered value. Never replaces
 * an authored value; never mutates the ledger entry.
 */
export function effectiveEffect(effect: ContainerEffect, containerUuid: string | null | undefined): ContainerEffect {
    if (!isEnterable(effect?.configuration)) return effect;
    const value = enteredValueFor(effect, containerUuid);
    if (value === null) return effect;
    return { ...effect, configuration: { ...effect.configuration, value } };
}

/**
 * The effect a row's field binds to: the LAST entry in ledger order on `path` with `detail` whose
 * effectTargets include `container`, returned only if it is enterable. Last in ledger order is what
 * rebuildContainerOverrides' lastWins fold uses, so the field and the fold always agree — an
 * authored value later in the ledger hides the field because it would win anyway. effectTargets
 * resolves a dynamic All Specified entry now and a frozen entry by its stored list, so both qualify.
 */
export function enterableEffectFor(
    character: CharacterSheetInterface,
    path: string,
    container: { uuid?: unknown } | null | undefined,
    detail: string,
): ContainerEffect | null {
    const uuid = container?.uuid;
    if (typeof uuid !== 'string' || uuid.length === 0) return null;
    const ledger: unknown = (character as { additionalData?: { containerEffects?: unknown } } | null | undefined)?.additionalData?.containerEffects;
    const effects = Array.isArray(ledger) ? ledger as ContainerEffect[] : [];
    let last: ContainerEffect | null = null;
    for (const effect of effects) {
        const configuration = effect?.configuration;
        if (configuration?.path !== path || configuration?.detail !== detail) continue;
        if (!effectTargets(character, effect).some((target: { uuid?: unknown }) => target?.uuid === uuid)) continue;
        last = effect;
    }
    return last !== null && isEnterable(last.configuration) ? last : null;
}

/**
 * What this modification calls the value it is asking the player for, or null when it says nothing
 * and the caller should fall back to its own generic word (the row does: only a component can read
 * the ux label tree, and this module stays pure).
 *
 * The author's own Label first. On a PICKED-target entry that label is the container display name
 * targetedConfiguration wrote, not the author's prose, and it still wins (ruling 2026-09-17 a): one
 * rule with no special case beats recognising a picker's write by comparing the label with the row's
 * display name. Otherwise the label of the authored chip THIS container matches — so an All
 * Specified modification listing several elements names each row by its own element rather than by
 * element[0].
 *
 * A bare-string element entry yields nothing on purpose: it carries a uuid and no label, and a raw
 * uuid is not a name to show a player. That is also what keeps a legacy bySelection string list from
 * surfacing one.
 * A chip labelled with its own uuid — the shape the configurator materialises for a legacy string
 * list, or a nameless catalog element — is refused for the same reason (whole-branch review,
 * 2026-09-17).
 */
export function enterableFieldLabel(
    effect: ContainerEffect | null | undefined,
    container: unknown,
): string | null {
    const authored = effect?.configuration?.label;
    if (typeof authored === 'string' && !isBlankString(authored)) return authored.trim();

    const wanted = selectionUuid(container);
    if (wanted === null) return null;

    const element = effect?.configuration?.element;
    if (element === null || element === undefined) return null;
    for (const entry of (Array.isArray(element) ? element : [element])) {
        if (elementEntryUuid(entry) !== wanted) continue;
        const label = (entry as { label?: unknown } | null)?.label;
        if (typeof label === 'string' && !isBlankString(label) && label.trim() !== wanted) return label.trim();
    }
    return null;
}
